/**
 * Sheet 30, downloads (mission 6 WP09 T049; FR-018, FR-016, FR-023, FR-026,
 * C-005, C-009, C-013; D.1, D.2, T.2, A.5, X.10, GEN.1, GEN.6, SEC.7; research
 * §1.14, §1.19; post-tasks P6, R12, E2; DM-01M3BGTHX2A3ERXRZPEMVE5EEM,
 * DM-01M3C8MP7AV3AWFNA6R365X21T).
 *
 * In order: the title row, the T.2 band (callout 30/2), the file rows (the
 * generated file on Path A only, the upload, the findings CSV; 30/3–30/5),
 * the audit-log notice as plain text (30/6), the retention line only when
 * `expires_at` is set (30/7), then Transmit, always secondary (30/1).
 *
 * - The generated row is labelled by run; its SHA-256 and size come from
 *   `run.generated`, so rendering issues nothing (an issuance is an audit
 *   event). The saved file takes the issuance body's `filename`.
 * - Every byte moves through `lib/files`; no URL reaches state or the DOM.
 * - Exactly one primary per page (R12): the generated Download on Path A, the
 *   upload's Download on Path B, where Trellis generated no file (X.10).
 * - A run that is not ready renders no file row, no primary Download and no
 *   Transmit: the API answers 409 for every object before ready (GEN.4), so
 *   the page says so in register 16's `runNotReady` and links back to the run
 *   (mission 6 review follow-up 22, A-5). Nothing is invalidated after a save:
 *   the history reads `['runs']` with `staleTime: 0` on its next mount.
 * - Mission 7 WP11 (T059; FR-016; sheet 30 callouts 8 and 10; squad U22): a
 *   ready Path A run renders the deliver-yourself panel after Transmit, which
 *   reads the bureaus. Path B and a run that is not ready render no panel and
 *   issue no bureaus request.
 * - Portal links (30/9, waiting for Legal), the PDF summary and an Auditor
 *   variant are absent (C-009, C-008): later missions own them.
 * - Mission 8 WP10 (T051; FR-020 A-L1, A-L3, A-L9; B.3, D.8, mission 6
 *   NFR-005): this page observes the run poll, so a live → terminal change of
 *   the same run seen here refreshes credits once, as RunPage does; a terminal
 *   first load, including another run's after a :runId change, does not.
 *   Loading and a failed read keep the page title as the `h1` above their
 *   state; not-found renders as RunPage renders it (its own `h1`, then the
 *   blocked band). The retention line's date is a `<time dateTime>` (P4).
 * - Mission 10 WP18 (T106, T107; FR-019 30/7 and 30/11, FR-007, FR-022; C-001,
 *   C-007, C-013; spec amendments 19 and 21; D.2, UC-04; OR-15, U17, U19, U31):
 *   a ready run reads the retention window's first page (`retentionQuery()`;
 *   `GET /v1/tenant` is not extended). This screen gate is the acknowledgment's
 *   only gate (C-001 leaves issuance unchanged), so it fails closed (cycles 2
 *   and 3, orchestrator rulings): while the read is pending the generated row's
 *   action slot holds a Skeleton, a non-button placeholder, and after a failed
 *   read it holds no control, only `genericError` in that row. Nothing claims
 *   an acknowledgment there is nothing yet to acknowledge. The upload and
 *   findings rows are unaffected.
 *   - At `retention_days = 0` on Path A, `RetentionZeroWarning` with its
 *     acknowledgment stands above the generated row, and until it is ticked the
 *     generated Download is `RetentionZeroGatedAction` (only in this state):
 *     secondary, relabelled
 *     `retentionZeroDownloadGated`, issuing nothing (§7.1, §11; U17). Ticked, it
 *     is the mission 6 `DownloadButton`, so the issuance request is unchanged
 *     (a screen gate only; C-001). The upload and findings rows are unaffected.
 *   - When the shell already shows its billing band (`billingBandShown`, the
 *     banner's own predicate: read-only or dunning) the page would stack three
 *     with T.2, which is never dismissible, so the warning renders inline
 *     inside the generated row's card (§7.5; U31).
 *   - `RetentionExpiryNote` (30/7's second part) beside the retention line.
 * - Mission 10 review UI1: a run whose `class1_deleted_at` is set offers no file: each row states
 *   `removedUnderRetention` (the existing 410 copy) in place of its action, and the retention-0 warning is not shown.
 */
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useId, useRef, useState, type ReactNode } from 'react'
import { Link, useParams } from 'react-router-dom'

import { Band } from '@/components/trellis/Band'
import { ExportButton } from '@/components/trellis/ExportButton'
import { ProblemBand } from '@/components/trellis/ProblemBand'
import { RetentionZeroGatedAction, RetentionZeroWarning } from '@/components/trellis/RetentionZeroWarning'
import { T2Band } from '@/components/trellis/T2Band'
import { TransmitControl } from '@/components/trellis/TransmitControl'
import { Alert, AlertTitle } from '@/components/ui/alert'
import { Skeleton } from '@/components/ui/skeleton'
import { ApiError, InvalidIdError } from '@/lib/api'
import { isTerminal, type Run, type RunState } from '@/lib/api/runs'
import { Mono, bytes, bytesExact, date, isoDateTime, sha256, shortId } from '@/lib/format'
import { PROVISIONAL_COPY, copyText } from '@/lib/provisional-copy'
import { creditsQuery, invalidateCredits, retentionQuery, runQuery } from '@/lib/queries'

import { billingBandShown } from '../shell/BillingBanner'
import { DeliverYourself } from './download/DeliverYourself'
import { DownloadButton, FileRow, type FileFact } from './download/FileRow'
import { RetentionExpiryNote } from './download/RetentionExpiryNote'

/** Register 17's run label with the short id in mono. */
function RunLabel({ runId }: { runId: string }) {
  const label = PROVISIONAL_COPY.runLabel.render({ runId })
  const id = shortId(runId)
  const at = label.indexOf(id)
  if (at < 0) return <>{label}</>
  return (
    <>
      {label.slice(0, at)}
      <Mono>{id}</Mono>
      {label.slice(at + id.length)}
    </>
  )
}

function sizeFact(size: number): FileFact {
  return { label: 'Size', value: bytes(size), ariaLabel: bytesExact(size) }
}

/** The page frame: the title as the one `h1`, then the page's state (A-L9). */
function Page({ title, children }: { title: ReactNode; children: ReactNode }) {
  return (
    <div className="flex max-w-4xl flex-col gap-6">
      <h1 className="text-h1 text-navy-950">{title}</h1>
      {children}
    </div>
  )
}

function Title({ runId }: { runId: string }) {
  return (
    <>
      {copyText('crumb.download')} · <RunLabel runId={runId} />
    </>
  )
}

function Loading() {
  return (
    <>
      <Skeleton className="h-16 w-full rounded-lg" />
      <Skeleton className="h-24 w-full rounded-lg" />
      <Skeleton className="h-24 w-full rounded-lg" />
      <Skeleton className="h-24 w-full rounded-lg" />
    </>
  )
}

function isNotFound(error: unknown): boolean {
  return error instanceof InvalidIdError || (error instanceof ApiError && error.problem.status === 404)
}

/** Not-found, as RunPage renders it (A-L9). */
function NotFound() {
  return (
    <Page title={copyText('notFoundTitle')}>
      <Alert variant="blocked">
        <AlertTitle>{copyText('notFound')}</AlertTitle>
      </Alert>
    </Page>
  )
}

function ReadFailure({ error }: { error: unknown }) {
  if (error instanceof ApiError) return <ProblemBand problem={error.problem} />
  return <Band kind="blocked">{copyText('genericError')}</Band>
}

/** Register 13's retention line with its date in `<time dateTime>` (A-L3, P4). */
function RetentionLine({ expiresAt }: { expiresAt: string }) {
  const shown = date(expiresAt)
  const text = PROVISIONAL_COPY.retentionLine.render({ expiresAt: shown })
  const at = text.indexOf(shown)
  return (
    <p className="text-body-sm text-navy-600">
      {at < 0 ? (
        text
      ) : (
        <>
          {text.slice(0, at)}
          <time dateTime={isoDateTime(expiresAt)}>{shown}</time>
          {text.slice(at + shown.length)}
        </>
      )}
    </p>
  )
}

/** The retention-0 variant (30/11): the warning's periods from `terms` (FR-022), and whether it goes inline (U31). */
interface RetentionZero {
  hours: number
  minutes: number
  inline: boolean
}

/**
 * What the generated download waits for (30/11): nothing (a window above 0, or Path B), the window read while it
 * is pending or after it failed (fail closed; cycle 2), or the retention-0 acknowledgment.
 */
type GeneratedGate = { kind: 'open' } | { kind: 'unread'; failed: boolean } | ({ kind: 'zero' } & RetentionZero)

const OPEN: GeneratedGate = { kind: 'open' }

/** The generated row's action, the warning above it, and what its card says beneath its facts. */
function useGeneratedDownload(runId: string, gate: GeneratedGate): { warning: ReactNode; action: ReactNode; note: ReactNode } {
  const ackId = useId()
  const [acknowledged, setAcknowledged] = useState(false)
  if (gate.kind === 'open') return { warning: null, action: <DownloadButton runId={runId} object="generated" primary />, note: null }
  // Cycle 3: unread, nothing is offered: a button-sized placeholder while pending, the failure sentence after.
  if (gate.kind === 'unread') {
    if (gate.failed) return { warning: null, action: null, note: <p className="text-body-sm text-navy-600">{copyText('genericError')}</p> }
    return { warning: null, action: <Skeleton className="h-9 w-28" />, note: null }
  }
  // Retention 0, unacknowledged: the gated action runs nothing and issues nothing (C-001: issuance is DownloadButton's).
  const gated = (
    <RetentionZeroGatedAction gate="download" acknowledged={false} acknowledgmentId={ackId} onClick={() => undefined}>
      Download
    </RetentionZeroGatedAction>
  )
  const warning = (
    <RetentionZeroWarning
      hours={gate.hours}
      minutes={gate.minutes}
      inline={gate.inline}
      acknowledgment={{ id: ackId, checked: acknowledged, onCheckedChange: setAcknowledged }}
    />
  )
  const action = acknowledged ? <DownloadButton runId={runId} object="generated" primary /> : gated
  return gate.inline ? { warning: null, action, note: warning } : { warning, action, note: null }
}

/** A row whose file the sweep removed: no action, the removal in the existing 410 copy (mission 10 review UI1). */
const REMOVED = { action: null, note: <p className="text-body-sm text-navy-600">{copyText('removedUnderRetention')}</p> }

function Files({ run, gate }: { run: Run; gate: GeneratedGate }) {
  const pathA = run.path === 'generate'
  const generatedFacts: FileFact[] = run.generated
    ? [{ label: 'SHA-256', value: sha256(run.generated.sha256) }, sizeFact(run.generated.size)]
    : []
  const uploadFacts: FileFact[] = run.upload ? [sizeFact(run.upload.size_bytes)] : []
  const live = useGeneratedDownload(run.run_id, pathA ? gate : OPEN)
  // Mission 10 review UI1 (FR-019, D.2): once the run's Class 1 is gone every file is, so no row offers its
  // download, the findings export or the retention-0 acknowledgment; each states the removal instead.
  const gone = Boolean(run.class1_deleted_at)
  const generated = gone ? { warning: null, ...REMOVED } : live
  return (
    <div data-download-files="" className="flex flex-col gap-3">
      {generated.warning}
      {pathA ? (
        <FileRow
          kind="generated"
          label={<RunLabel runId={run.run_id} />}
          description={copyText('fileRow.generated')}
          facts={generatedFacts}
          action={generated.action}
          {...(generated.note !== null ? { note: generated.note } : {})}
        />
      ) : (
        <Band kind="info">{copyText('pathBNotGenerated')}</Band>
      )}
      <FileRow
        kind="upload"
        label={run.upload ? <Mono>{run.upload.filename}</Mono> : undefined}
        description={copyText('fileRow.upload')}
        facts={uploadFacts}
        {...(gone ? REMOVED : { action: <DownloadButton runId={run.run_id} object="upload" primary={!pathA} /> })}
      />
      <FileRow
        kind="findings"
        description={copyText('fileRow.findingsCsv')}
        {...(gone ? REMOVED : { action: <ExportButton runId={run.run_id} /> })}
      />
    </div>
  )
}

/** Before ready there is nothing to download or transmit (GEN.4; A-5). */
function NotReady({ run }: { run: Run }) {
  return (
    <Band
      kind="info"
      action={
        <Link
          to={`/app/runs/${encodeURIComponent(run.run_id)}`}
          className="text-body-sm text-[var(--color-link)] hover:underline"
        >
          <RunLabel runId={run.run_id} />
        </Link>
      }
    >
      <p>{PROVISIONAL_COPY.runNotReady.render({ state: run.state })}</p>
    </Band>
  )
}

function ReadyBody({ run }: { run: Run }) {
  // T106, T107: the window from the retention read's first page; the billing band from the credits read
  // TransmitControl already observes (no extra read).
  const read = useInfiniteQuery(retentionQuery())
  const retention = read.data?.pages[0]
  const billing = useQuery(creditsQuery()).data?.billing
  const gate: GeneratedGate =
    retention === undefined
      ? { kind: 'unread', failed: read.isError }
      : retention.retention_days === 0
        ? {
            kind: 'zero',
            hours: retention.terms.zero_fallback_hours,
            minutes: retention.terms.sweep_interval_minutes,
            inline: billingBandShown(billing),
          }
        : OPEN
  return (
    <>
      <Files run={run} gate={gate} />
      <div className="flex flex-col gap-1">
        <p className="text-body-sm text-navy-600">{copyText('auditLogNotice')}</p>
        {run.expires_at ? <RetentionLine expiresAt={run.expires_at} /> : null}
        {run.expires_at && retention ? <RetentionExpiryNote retention={retention} /> : null}
      </div>
      <div>
        <TransmitControl run={run} />
      </div>
      {run.path === 'generate' ? <DeliverYourself /> : null}
    </>
  )
}

export default function Download() {
  const { runId = '' } = useParams()
  const qc = useQueryClient()
  const query = useQuery(runQuery(runId))
  const observedId = query.data?.run_id
  const state = query.data?.state

  // A-L1: the same record RunPage keeps (RunPage.tsx): only a live → terminal
  // change of the same run refreshes credits; a terminal first load does not.
  const observed = useRef<{ id: string; state: RunState } | undefined>(undefined)
  useEffect(() => {
    if (observedId === undefined || state === undefined) return
    const previous = observed.current
    observed.current = { id: observedId, state }
    if (previous?.id === observedId && !isTerminal(previous.state) && isTerminal(state)) void invalidateCredits(qc)
  }, [observedId, state, qc])

  if (query.data === undefined) {
    if (query.isError && isNotFound(query.error)) return <NotFound />
    return <Page title={<Title runId={runId} />}>{query.isError ? <ReadFailure error={query.error} /> : <Loading />}</Page>
  }
  const run = query.data
  return (
    <Page title={<Title runId={run.run_id} />}>
      <T2Band />
      {run.state === 'ready' ? <ReadyBody run={run} /> : <NotReady run={run} />}
    </Page>
  )
}
